import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Calendar, ArrowLeft, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsightDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching post:", error);
      if (error.response?.status === 404) {
        toast.error("Insight not found");
        navigate("/insights");
      } else {
        toast.error("Failed to load insight");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen noise-texture pt-32 flex items-center justify-center" data-testid="loading-state">
        <p className="text-gray-400">Loading insight...</p>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen noise-texture pt-32">
      <article className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/insights"
            data-testid="back-button"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-brandPrimary transition-colors mb-8 group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            Back to Insights
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
              <span className="px-4 py-1.5 bg-brandPrimary/10 text-brandPrimary rounded-full font-medium" data-testid="post-category">
                {post.category}
              </span>
              <div className="flex items-center gap-1" data-testid="post-read-time">
                <Clock size={16} />
                <span>{post.read_time} min read</span>
              </div>
            </div>

            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6" data-testid="post-title">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-gray-400">
              <div className="flex items-center gap-2" data-testid="post-author">
                <User size={18} />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2" data-testid="post-date">
                <Calendar size={18} />
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>
          </motion.div>

          {/* Excerpt */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="glass-card rounded-2xl p-8 mb-12"
          >
            <p className="text-gray-300 text-lg leading-relaxed" data-testid="post-excerpt">
              {post.excerpt}
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-invert prose-lg max-w-none"
            data-testid="post-content"
          >
            <div className="text-gray-300 leading-relaxed space-y-6">
              {post.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </motion.div>

          {/* CTA Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 glass-card rounded-2xl p-8 text-center"
          >
            <h3 className="font-heading font-semibold text-2xl text-white mb-4" data-testid="post-cta-heading">
              Want Strategic Help for Your Brand?
            </h3>
            <p className="text-gray-400 mb-6" data-testid="post-cta-description">
              Let's discuss how we can apply these insights to your business.
            </p>
            <Link
              to="/contact"
              data-testid="post-cta-button"
              className="inline-block bg-brandPrimary hover:bg-brandPrimary/90 text-white px-8 py-3 rounded-full font-medium transition-all"
            >
              Book a Strategy Call
            </Link>
          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default InsightDetailPage;